// /plans — admin lista de planos comerciais (Story 2.5a).
// Form modal único (não wizard). Wizard 3 passos vem na 2.5b.

import * as Dialog from '@radix-ui/react-dialog';
import {
  AlertCircle,
  FileText,
  Package2,
  Pencil,
  Plus,
  Search,
  Tag,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type {
  CreatePlanDto,
  CreatePlanInput,
  PlanStatusSchema,
  PlanTypeSchema,
  QueryPlansDto,
} from '@control-center/shared';

import { cn } from '@control-center/ui';

import { Field, Input, Select } from '../../components/clients/form-primitives';
import { PillButton } from '../../components/clients/PillButton';
import { EmptyState as RichEmptyState } from '../../components/common/EmptyState';
import { PageHelp } from '../../components/common/PageHelp';
import { PlanForm } from '../../components/plans/PlanForm';
import { useExternalPlans } from '../../hooks/use-external-plans';
import {
  useCreatePlan,
  useDeletePlan,
  useHardDeletePlan,
  usePlans,
  useUpdatePlan,
} from '../../hooks/use-plans';
import { useSaasProducts } from '../../hooks/use-saas-products';
import { extractErrorMessage } from '../../lib/errors';
import type { PlanListItem } from '../../lib/plans-api';
import { useAuthStore } from '../../stores/useAuthStore';

const TYPE_LABEL: Record<PlanTypeSchema, string> = {
  single_saas: 'Single SaaS',
  bundle: 'Bundle',
  service_only: 'Service-only',
};

const STATUS_LABEL: Record<PlanStatusSchema, string> = {
  ativo: 'Ativo',
  oculto: 'Oculto',
  legado: 'Legado',
  descontinuado: 'Descontinuado',
};

const STATUS_DOT: Record<PlanStatusSchema, string> = {
  ativo: 'bg-good ring-good/15',
  oculto: 'bg-muted-2 ring-muted-3/15',
  legado: 'bg-warn ring-warn/15',
  descontinuado: 'bg-bad ring-bad/15',
};

export function PlansPage(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';
  const navigate = useNavigate();

  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | PlanTypeSchema>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | PlanStatusSchema>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<PlanListItem | null>(null);

  const filters: QueryPlansDto = useMemo(() => {
    const out: QueryPlansDto = { limit: 50 };
    if (q.trim()) out.q = q.trim();
    if (typeFilter !== 'all') out.type = typeFilter;
    if (statusFilter !== 'all') out.status = statusFilter;
    return out;
  }, [q, typeFilter, statusFilter]);

  const list = usePlans(filters);
  const saasList = useSaasProducts({ limit: 100 });
  const externalsList = useExternalPlans({ limit: 100 });
  const create = useCreatePlan();
  const update = useUpdatePlan();
  const remove = useDeletePlan();
  const hardRemove = useHardDeletePlan();

  const saasProducts = saasList.data?.items ?? [];
  const externalPlans = externalsList.data?.items ?? [];

  function openNew(): void {
    // 2.5b: criação via wizard (3 passos)
    navigate('/plans/new');
  }

  function openEdit(p: PlanListItem): void {
    // Modal antigo continua disponível pra edit rápido (sem entitlements)
    setEditing(p);
    setFormOpen(true);
  }

  function openDetail(id: string): void {
    navigate(`/plans/${id}`);
  }

  async function handleSubmit(values: CreatePlanDto): Promise<void> {
    try {
      if (editing) {
        // Em EDIT: PATCH sem entitlements/slug (imutáveis em 2.5a)
        const { slug: _slug, entitlements: _ent, ...rest } = values;
        void _slug;
        void _ent;
        await update.mutateAsync({
          id: editing.id,
          payload: { ...rest, version: editing.version },
        });
        toast.success('Plano atualizado.');
      } else {
        await create.mutateAsync(values);
        toast.success('Plano criado.');
      }
      setFormOpen(false);
      setEditing(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos salvar.'));
    }
  }

  async function handleDelete(p: PlanListItem): Promise<void> {
    const ok = window.confirm(
      `Descontinuar "${p.name}"? Status muda pra "descontinuado". Se houver assinaturas ativas, a ação será bloqueada.`,
    );
    if (!ok) return;
    try {
      await remove.mutateAsync({ id: p.id, version: p.version });
      toast.success('Plano descontinuado.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos descontinuar o plano.'));
    }
  }

  async function handleHardDelete(p: PlanListItem): Promise<void> {
    const ok = window.confirm(
      `EXCLUIR DEFINITIVAMENTE "${p.name}"?\n\nO plano será apagado do banco, junto com entitlements e histórico de preço. Essa ação NÃO pode ser desfeita.\n\nSó funciona se não houver nenhuma assinatura (mesmo cancelada) vinculada.`,
    );
    if (!ok) return;
    try {
      await hardRemove.mutateAsync({ id: p.id, version: p.version });
      toast.success('Plano excluído definitivamente.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos excluir o plano.'));
    }
  }

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="m-0 text-[26px] font-bold tracking-[-0.025em] text-ink">
              Planos comerciais
            </h1>
            <p className="mt-1 text-[13px] text-muted">
              Planos vendidos no Control Center: single SaaS, bundles (Owl Suite) e serviços.
            </p>
          </div>
          <PageHelp id="plans" />
        </div>
        {isAdmin && (
          <PillButton solid icon={<Plus size={14} strokeWidth={2} />} onClick={openNew}>
            Novo plano
          </PillButton>
        )}
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <Field label="Buscar">
            {({ inputId }) => (
              <div className="relative">
                <Search
                  size={14}
                  strokeWidth={2}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-2"
                />
                <Input
                  id={inputId}
                  type="text"
                  placeholder="Nome ou slug"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-9"
                />
              </div>
            )}
          </Field>
        </div>
        <div className="min-w-[180px]">
          <Field label="Tipo">
            {({ inputId }) => (
              <Select
                id={inputId}
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as 'all' | PlanTypeSchema)}
              >
                <option value="all">Todos</option>
                {(['single_saas', 'bundle', 'service_only'] as const).map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <div className="min-w-[160px]">
          <Field label="Status">
            {({ inputId }) => (
              <Select
                id={inputId}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | PlanStatusSchema)}
              >
                <option value="all">Todos</option>
                {(['ativo', 'oculto', 'legado', 'descontinuado'] as const).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </div>

      {list.isLoading ? (
        <SkeletonList />
      ) : list.isError ? (
        <ErrorBox
          message={extractErrorMessage(list.error, 'Não conseguimos carregar.')}
          onRetry={() => void list.refetch()}
        />
      ) : list.data && list.data.items.length > 0 ? (
        <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
          <table className="w-full text-[13px]">
            <thead className="border-b border-line bg-panel-2">
              <tr className="text-left text-[11px] uppercase tracking-wider text-muted-2">
                <th className="px-4 py-3 font-medium">Plano</th>
                <th className="px-4 py-3 font-medium">Tipo</th>
                <th className="px-4 py-3 font-medium tnum">Preço</th>
                <th className="px-4 py-3 font-medium tnum">Entitlements</th>
                <th className="px-4 py-3 font-medium">Status</th>
                {isAdmin && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((p) => (
                <tr
                  key={p.id}
                  className="cursor-pointer border-b border-line last:border-b-0 hover:bg-panel-2"
                  onClick={() => openDetail(p.id)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-panel-3 text-muted">
                        <Package2 size={14} strokeWidth={1.8} />
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-medium text-ink">{p.name}</span>
                          {p.autoEmitNf && (
                            <span
                              title="Emite NFS-e automaticamente após pagamento"
                              className="inline-flex shrink-0 items-center gap-1 rounded-full bg-panel-3 px-2 py-[2px] text-[10px] font-medium text-muted ring-1 ring-line"
                            >
                              <FileText size={10} strokeWidth={2} />
                              NF auto
                            </span>
                          )}
                        </div>
                        <code className="block truncate font-mono text-[11px] text-muted-2">
                          {p.slug}
                        </code>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted">{TYPE_LABEL[p.type]}</td>
                  <td className="px-4 py-3 tnum">
                    <div className="text-ink">{formatBRL(p.monthlyPrice)}/mês</div>
                    {p.annualPrice && (
                      <div className="text-[11px] text-muted-2">{formatBRL(p.annualPrice)}/ano</div>
                    )}
                  </td>
                  <td className="px-4 py-3 tnum text-muted">{p._count.entitlements}</td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-flex items-center gap-[6px] text-[11px] font-medium',
                        p.status === 'ativo' ? 'text-ink-2' : 'text-muted',
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          'h-[7px] w-[7px] rounded-full ring-[3px]',
                          STATUS_DOT[p.status],
                        )}
                      />
                      {STATUS_LABEL[p.status]}
                    </span>
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          aria-label={`Edit rápido ${p.name}`}
                          title="Edit rápido (sem entitlements)"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(p);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-ink"
                        >
                          <Pencil size={13} strokeWidth={1.8} />
                        </button>
                        <button
                          type="button"
                          aria-label={
                            p.status === 'descontinuado'
                              ? `Excluir definitivamente ${p.name}`
                              : `Descontinuar ${p.name}`
                          }
                          title={
                            p.status === 'descontinuado'
                              ? 'Excluir definitivamente (apaga do banco)'
                              : 'Descontinuar'
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            if (p.status === 'descontinuado') {
                              void handleHardDelete(p);
                            } else {
                              void handleDelete(p);
                            }
                          }}
                          className={cn(
                            'flex h-8 w-8 items-center justify-center rounded-[6px] transition-colors',
                            p.status === 'descontinuado'
                              ? 'text-bad/70 hover:bg-bad/10 hover:text-bad'
                              : 'text-muted hover:bg-panel-3 hover:text-bad',
                          )}
                        >
                          <Trash2 size={13} strokeWidth={1.8} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          isAdmin={isAdmin}
          hasFilters={Boolean(q || typeFilter !== 'all' || statusFilter !== 'all')}
          onAdd={openNew}
        />
      )}

      {/* Dialog form */}
      {isAdmin && (
        <Dialog.Root open={formOpen} onOpenChange={setFormOpen}>
          <Dialog.Portal>
            <Dialog.Overlay
              className={cn(
                'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              )}
            />
            <Dialog.Content
              className={cn(
                'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
                'w-[min(800px,calc(100vw-32px))] max-h-[calc(100vh-48px)] overflow-y-auto',
                'rounded-[14px] border border-line bg-panel shadow-lg p-7',
                'focus:outline-none',
              )}
            >
              <div className="mb-5 flex items-start justify-between gap-4">
                <Dialog.Title className="text-[20px] font-semibold tracking-[-0.01em] text-ink">
                  {editing ? `Editar ${editing.name}` : 'Novo plano comercial'}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label="Fechar"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
                  >
                    <X size={15} strokeWidth={2} />
                  </button>
                </Dialog.Close>
              </div>

              <PlanForm
                editMode={Boolean(editing)}
                saasProducts={saasProducts}
                externalPlans={externalPlans}
                defaultValues={editing ? toFormDefaults(editing) : undefined}
                submitLabel={editing ? 'Salvar alterações' : 'Criar plano'}
                isPending={create.isPending || update.isPending}
                onSubmit={handleSubmit}
                onCancel={() => setFormOpen(false)}
              />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
}

// =============================================================================
// Sub-components / helpers
// =============================================================================

function SkeletonList(): JSX.Element {
  return (
    <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          aria-hidden
          className="h-[60px] animate-pulse border-b border-line bg-panel-2 last:border-b-0"
        />
      ))}
    </div>
  );
}

function ErrorBox({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-bad/30 bg-bad/5 p-5 text-[13px]">
      <div className="flex items-center gap-2 font-medium text-bad">
        <AlertCircle size={14} strokeWidth={2} aria-hidden />
        Não conseguimos carregar os planos.
      </div>
      <p className="mt-1 text-muted">{message}</p>
      <div className="mt-3">
        <PillButton onClick={onRetry}>Tentar de novo</PillButton>
      </div>
    </div>
  );
}

function EmptyState({
  isAdmin,
  hasFilters,
  onAdd,
}: {
  isAdmin: boolean;
  hasFilters: boolean;
  onAdd: () => void;
}): JSX.Element {
  if (hasFilters) {
    return (
      <div className="rounded-[14px] border border-line bg-panel-2 p-6 text-center">
        <p className="text-[13px] text-muted">Nenhum plano bate com os filtros.</p>
      </div>
    );
  }
  return (
    <RichEmptyState
      icon={Tag}
      title="Sem planos cadastrados"
      description="Planos comerciais definem o que o cliente vai pagar, com qual periodicidade e o que ele recebe nos SaaS."
      bullets={[
        'Single SaaS — ex: "Reservei Pro R$ 99/mês"',
        'Bundle (Owl Suite) — junta vários SaaS num plano só',
        'Service-only — implantação, consultoria, sem recorrência',
      ]}
      primaryAction={
        isAdmin
          ? {
              label: 'Cadastrar primeiro plano',
              icon: <Plus size={14} strokeWidth={2} />,
              onClick: onAdd,
            }
          : undefined
      }
    />
  );
}

function formatBRL(value: string): string {
  const n = Number(value);
  if (Number.isNaN(n)) return 'R$ —';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function toFormDefaults(p: PlanListItem): Partial<CreatePlanInput> {
  return {
    slug: p.slug,
    name: p.name,
    description: p.description ?? '',
    type: p.type,
    monthly_price: Number(p.monthlyPrice),
    annual_price: p.annualPrice != null ? Number(p.annualPrice) : null,
    setup_fee: Number(p.setupFee),
    promo_price: p.promoPrice != null ? Number(p.promoPrice) : null,
    billing_cycle: p.billingCycle,
    discount_annual_pct: p.discountAnnualPct != null ? Number(p.discountAnnualPct) : null,
    trial_days: p.trialDays,
    requires_card_for_trial: p.requiresCardForTrial,
    blocks_repeat_trial: p.blocksRepeatTrial,
    limits: p.limits ?? {},
    features: p.features ?? {},
    checkout_highlights: p.checkoutHighlights ?? [],
    status: p.status,
    entitlements: [], // edit não mexe em entitlements (badge 2.5b)
  };
}
